import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chip,
  InputAdornment,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  Assignment,
  MailOutline,
  Photo as PhotoIcon,
  Search,
} from "@mui/icons-material";
import TopNav from "../components/TopNav";
import { FeedItem, getActivityFeed } from "../api/activities";
import { APIProposalStatus } from "../api/proposals";
import { statusToColor } from "../utils";
import pageStyle from "./Activity.module.css";

const KINDS: { kind: FeedItem["kind"]; label: string; icon: ReactNode }[] = [
  { kind: "proposal", label: "Proposals", icon: <Assignment /> },
  { kind: "appointment", label: "Appointments", icon: <AccessTime /> },
  { kind: "photo", label: "Photos", icon: <PhotoIcon /> },
  { kind: "email", label: "Emails", icon: <MailOutline /> },
];

const RANGES = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 0, label: "All" },
];

const DAY = 86400000;

function dayLabel(ms: number) {
  const d = new Date(ms);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const diff = Math.floor((start.getTime() - new Date(d).setHours(0, 0, 0, 0)) / DAY);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff === -1) return "Tomorrow";
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function dayKey(ms: number) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function FeedRow({ item, showCustomer }: { item: FeedItem; showCustomer: boolean }) {
  const icon = KINDS.find((k) => k.kind === item.kind)?.icon;
  return (
    <Link to={item.link} className={pageStyle.row}>
      <span className={pageStyle.icon}>{icon}</span>
      <div className={pageStyle.rowText}>
        <div className={pageStyle.rowTitle}>
          {showCustomer && item.customerName && (
            <strong>{item.customerName} · </strong>
          )}
          {item.title}
        </div>
        {item.detail && <div className={pageStyle.rowDetail}>{item.detail}</div>}
      </div>
      {item.photoData && (
        <img src={item.photoData} alt="" className={pageStyle.thumb} />
      )}
      {item.status && (
        <Chip
          size="small"
          variant="outlined"
          label={item.status}
          color={statusToColor(item.status as APIProposalStatus)}
        />
      )}
      <span className={pageStyle.time}>
        {new Date(item.when).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}
      </span>
    </Link>
  );
}

function ActivityListPage() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [view, setView] = useState<"day" | "customer">("day");
  const [kinds, setKinds] = useState<FeedItem["kind"][]>(KINDS.map((k) => k.kind));
  const [range, setRange] = useState(30);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getActivityFeed().then(setItems);
  }, []);

  const visible = useMemo(() => {
    const since = range ? Date.now() - range * DAY : 0;
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter(
      (i) =>
        kinds.includes(i.kind) &&
        i.when >= since &&
        terms.every((t) =>
          `${i.customerName} ${i.title} ${i.detail}`.toLowerCase().includes(t)
        )
    );
  }, [items, kinds, range, search]);

  // Groups in display order: newest day first, or most recently active
  // customer first.
  const groups = useMemo(() => {
    const map = new Map<string, { label: ReactNode; items: FeedItem[] }>();
    for (const i of visible) {
      const key = view === "day" ? dayKey(i.when) : i.customerId || i.customerName;
      if (!map.has(key)) {
        map.set(key, {
          label:
            view === "day" ? (
              dayLabel(i.when)
            ) : i.customerId ? (
              <Link to={`/customers?id=${i.customerId}`}>
                {i.customerName || "Unnamed customer"}
              </Link>
            ) : (
              i.customerName || "No customer"
            ),
          items: [],
        });
      }
      map.get(key)!.items.push(i);
    }
    return Array.from(map.entries());
  }, [visible, view]);

  const toggleKind = (k: FeedItem["kind"]) =>
    setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);

  return (
    <div className="page">
      <TopNav pageTitle="Activity" />
      <main className={pageStyle.main}>
        <div className={pageStyle.content}>
          <div className={pageStyle.controls}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_, v) => v && setView(v)}
            >
              <ToggleButton value="day">By day</ToggleButton>
              <ToggleButton value="customer">By customer</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={range}
              onChange={(_, v) => v !== null && setRange(v)}
            >
              {RANGES.map((r) => (
                <ToggleButton key={r.value} value={r.value}>
                  {r.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <OutlinedInput
              size="small"
              placeholder="Search customer or activity"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              className={pageStyle.search}
            />
          </div>
          <div className={pageStyle.kinds}>
            {KINDS.map((k) => (
              <Chip
                key={k.kind}
                icon={k.icon as any}
                label={k.label}
                variant={kinds.includes(k.kind) ? "filled" : "outlined"}
                color={kinds.includes(k.kind) ? "primary" : "default"}
                onClick={() => toggleKind(k.kind)}
              />
            ))}
          </div>

          {groups.length === 0 && (
            <Typography color="text.secondary">
              Nothing in this period.
            </Typography>
          )}
          {groups.map(([key, g]) => (
            <section key={key} className={pageStyle.group}>
              <div className={pageStyle.groupHeader}>
                <Typography variant="subtitle1">{g.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {g.items.length} item{g.items.length === 1 ? "" : "s"}
                </Typography>
              </div>
              {g.items.map((i) => (
                <FeedRow
                  key={`${i.kind}-${i.id}`}
                  item={i}
                  showCustomer={view === "day"}
                />
              ))}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}

export default ActivityListPage;
